import {useParams} from 'react-router-dom';
import styles from './recurringInvestment.module.scss';
import {
  GET_RECURRING_INVESTMENTS,
  CHANGE_RECURRING_INVESTMENT_STATUS,
  GET_USER_RECURRING_INVESTMENTS_STATS,
  GET_RECURRING_INVESTMENT_STATS,
} from './resolvers';
import {toast} from 'react-toastify';
import {useMutation, useQuery} from '@apollo/client';
import {DatePicker, Dropdown, Form, Select, Table, Tag, Divider} from 'antd';
import moment from 'moment';
import {useState} from 'react';
import DBModal from 'components/modal/modal';
import {Button} from '@dabafinance/react-components';
import {DetailedPercentageCard} from 'pages/adminUser/card/PercentageCard';
import {AnimatePresence, motion} from 'framer-motion';
import SubmitButton from 'components/Startup/components/SubmitButton';
import dotsIcon from 'images/dots.svg';
import {ConfirmModal} from 'pages/adminOpportunities/stocksListings/ViewOrders/HandleOrders';
import {RecurringInvestmentLogs} from './RecurringInvestmentsLogs';
import {useSelector} from 'react-redux';
import {makeSelectTabOperations} from 'redux/store/auth';
import {NavTabsEnum} from 'utils/constants';
import {CloseOutlined} from '@ant-design/icons';
import UserLink from 'components/userLink/UserLink';

export const RecurringInvestmentPage = ({id}) => {
  const [openFilterModal, setOpenFilterModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [status, setStatus] = useState({open: false, id: null, status: null});
  const [limit, setLimit] = useState(10);
  const [openModal, setOpenModal] = useState({
    open: false,
    data: null,
  });
  const operations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.RecurringInvestments),
  );

  const {data, refetch, loading} = useQuery(GET_RECURRING_INVESTMENTS, {
    variables: {
      filter: {
        userId: id,
        page: 1,
        limit: 10,
      },
    },
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
    onError: error => toast.error(error.message),
  });
  const statsResolver = id
    ? GET_USER_RECURRING_INVESTMENTS_STATS
    : GET_RECURRING_INVESTMENT_STATS;

  const {
    data: statsData,
    loading: statsLoading,
    refetch: refetchStats,
  } = useQuery(statsResolver, {
    variables: {
      userId: id,
    },
    errorPolicy: 'all',
    onError: error => toast.error(error.message),
  });

  const [changeRecurringInvestmentState, {loading: loadingChangeStatus}] =
    useMutation(CHANGE_RECURRING_INVESTMENT_STATUS, {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    });

  const handlePageChange = (page, pageSize) => {
    if (page === currentPage && pageSize === limit) return;
    setCurrentPage(page);
    setLimit(pageSize);
    refetch({
      filter: {
        page,
        limit: pageSize,
        userId: id,
      },
    });
  };

  const columns = [
    ...(id
      ? []
      : [
          {
            title: 'User',
            dataIndex: 'user',
            render: (text, record) => (
              <div>
                <div className={styles['record-label']}>
                  {record?.user?.imageUrl ? (
                    <img src={record?.user?.imageUrl} alt="" />
                  ) : (
                    <div className={styles['dummyAvatar']}>
                      {record?.user?.firstName?.charAt(0)}
                      {record?.user?.lastName?.charAt(0)}
                    </div>
                  )}
                  <h4>
                    <UserLink
                      userId={record?.user?.id}
                      tab="recurring-investment">
                      {record?.user?.firstName} {record?.user?.lastName}
                    </UserLink>
                  </h4>
                </div>
              </div>
            ),
          },
        ]),
    {
      title: 'Asset Type',
      dataIndex: 'assetType',
      render: (text, record) => {
        return (
          <Tag
            color={
              {
                'BOND': 'cyan',
                'STOCK': 'red',
                'MUTUAL_FUND': 'purple',
                'COLLECTION': 'blue',
              }[record?.assetType]
            }>
            {record?.assetType?.replace('_', ' ')}
          </Tag>
        );
      },
    },
    {
      title: 'Asset',
      dataIndex: 'asset',
      render: (text, record) => {
        return <p>{record?.symbol ?? 'N/A'}</p>;
      },
    },
    {
      title: 'Frequency',
      dataIndex: 'frequency',
      render: (text, record) => {
        return (
          <p style={{textTransform: 'capitalize'}}>
            {record?.frequency?.replace('_', ' ')}
          </p>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (text, record) => {
        return (
          <Tag color={record?.status === 'ACTIVE' ? 'green' : 'red'}>
            {record?.status}
          </Tag>
        );
      },
    },

    {
      title: 'Next Run Date',
      dataIndex: 'nextRunDate',
      render: (text, record) => {
        return <p>{moment(record?.nextRunDate).format('DD/MM/YYYY')}</p>;
      },
    },
    {
      title: 'Recurring Investment Amount',
      dataIndex: 'amount',
      render: (text, record) => {
        return (
          <p>
            {record?.currency} {record?.amount?.toLocaleString()}
          </p>
        );
      },
    },
    {
      title: 'Successful Investment Cycles',
      dataIndex: 'successfulPaymentCycles',
      render: (text, record) => {
        return <p>{record?.successfulPaymentCycles}</p>;
      },
    },
    {
      title: 'Failed Investment Cycles',
      dataIndex: 'failedPaymentCycles',
      render: (text, record) => {
        return <p>{record?.failedPaymentCycles}</p>;
      },
    },
    {
      title: 'Created On',
      dataIndex: 'createdAt',
      render: (text, record) => {
        return <p>{moment(record?.createdAt).format('DD/MM/YYYY, hh:mm A')}</p>;
      },
    },
    {
      title: 'Action',
      dataIndex: 'action',
      render: (text, record) => (
        <Dropdown
          menu={{
            items: [
              {
                label: 'Update Status',
                key: 1,
                disabled: !operations?.update,
                onClick: e => {
                  e.domEvent.stopPropagation();
                  setStatus({
                    open: true,
                    id: record?.id,
                    status: record?.status,
                  });
                },
              },
            ],
          }}>
          <img src={dotsIcon} alt="dots" onClick={e => e.stopPropagation()} />
        </Dropdown>
      ),
    },
  ];

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>{id ? 'User' : ''} Recurring Investments</h1>
        <div className={styles.filters}>
          <Button
            label="Filter"
            onClick={() => setOpenFilterModal(!openFilterModal)}
            className={styles.filterButton}
          />
          <AnimatePresence>
            <motion.div
              initial={{opacity: 0, y: -20}}
              style={{
                display: openFilterModal ? 'block' : 'none',
              }}
              animate={{opacity: 1, y: 0}}
              exit={{opacity: 0, y: -20}}
              transition={{duration: 0.5, ease: 'easeOut'}}
              className={styles.filterModal}>
              <Form
                onFinish={values => {
                  const {startDate, endDate} = values;
                  setCurrentPage(1);
                  setLimit(10);
                  refetch({
                    filter: {
                      ...values,
                      startDate: startDate?.toISOString(),
                      endDate: endDate?.toISOString(),
                      userId: id,
                      page: 1,
                      limit: 10,
                    },
                  });
                  setOpenFilterModal(false);
                }}
                layout="vertical"
                className={styles.filterForm}>
                <div className={styles.filterHeader}>
                  <h2>Filter Investments</h2>
                  <div
                    onClick={() => setOpenFilterModal(false)}
                    className={styles.closeFilter}>
                    <CloseOutlined />
                  </div>
                </div>
                <Form.Item label="Start Date" name="startDate">
                  <DatePicker showTime className={styles.dateField} />
                </Form.Item>
                <Form.Item label="End Date" name="endDate">
                  <DatePicker showTime className={styles.dateField} />
                </Form.Item>
                <Form.Item
                  className={styles.dateField}
                  label="Status"
                  name="status">
                  <Select
                    allowClear
                    placeholder="Select Status"
                    options={[
                      {label: 'Active', value: 'ACTIVE'},
                      {label: 'Inactive', value: 'INACTIVE'},
                    ]}
                  />
                </Form.Item>
                <SubmitButton label="Filter" />
              </Form>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      <div className={styles.stats}>
        {statsData?.getRecurringInvestmentStatistics?.data?.map(stat => (
          <DetailedPercentageCard
            loading={statsLoading}
            label={stat?.assetType?.replace('_', ' ')}
            amount={stat?.totalAmount}
            key={stat?.assetType}
            currency="CFA"
            additionalContent={
              <div className={styles.statsContainer}>
                <div className={styles.statItem}>
                  <p>
                    <strong>ACTIVE ({stat?.totalActiveCount})</strong>
                  </p>
                  <p>
                    <span>
                      <strong style={{fontSize: '12px'}}>CFA</strong>
                      {stat?.totalActiveAmount >= 100000000
                        ? stat?.totalActiveAmount?.toLocaleString(undefined, {
                            notation: 'compact',
                            maximumFractionDigits: 1,
                          })
                        : stat?.totalActiveAmount?.toLocaleString()}
                    </span>
                  </p>
                </div>
                <Divider
                  style={{
                    height: '40px',
                    borderLeft: '1px solid #8b82a7',
                    margin: '0 1px',
                  }}
                  type="vertical"
                />
                <div className={styles.statItem}>
                  <p>
                    <strong>INACTIVE ({stat?.totalInactiveCount})</strong>
                  </p>
                  <p>
                    <span>
                      <strong style={{fontSize: '12px'}}>CFA</strong>
                      {stat?.totalInactiveAmount >= 100000000
                        ? stat?.totalInactiveAmount?.toLocaleString(undefined, {
                            notation: 'compact',
                            maximumFractionDigits: 1,
                          })
                        : stat?.totalInactiveAmount?.toLocaleString()}
                    </span>
                  </p>
                </div>
              </div>
            }
          />
        ))}
      </div>
      <Table
        loading={loading}
        dataSource={data?.getRecurringInvestments?.data}
        columns={columns}
        className={styles.table}
        onRow={record => ({
          style: {
            cursor: 'pointer',
          },
          onClick: () => {
            setOpenModal({
              open: true,
              data: record,
            });
          },
        })}
        pagination={{
          current: currentPage,
          pageSize: limit,
          total: data?.getRecurringInvestments?.pagination?.total,
          onChange: handlePageChange,
          showSizeChanger: true,
        }}
      />
      <DBModal
        isOpen={openModal.open}
        handleClose={() => setOpenModal({open: false, data: null})}
        width={1000}
        content={<RecurringInvestmentLogs id={openModal.data?.id} />}
      />
      <ConfirmModal
        title="Update Status"
        confirmText={`Are you sure you want to Update this status to ${
          status?.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE'
        }?`}
        openModal={status?.open}
        setOpenModal={() => setStatus({open: false, id: null, status: null})}
        loading={loadingChangeStatus}
        handleOperation={() => {
          changeRecurringInvestmentState({
            variables: {
              changeRecurringInvestmentStateId: status?.id,
              state: status?.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE',
            },
          })
            .then(({data: {changeRecurringInvestmentState}}) => {
              if (
                changeRecurringInvestmentState?.statusCode === 400 ||
                changeRecurringInvestmentState.__typename === 'Error'
              ) {
                toast.error(changeRecurringInvestmentState?.message);
              } else {
                toast.success(changeRecurringInvestmentState?.message);
                setStatus({open: false, id: null, status: null});
                refetch();
                refetchStats();
              }
            })
            .catch(error => toast.error(error.message));
        }}
      />
    </div>
  );
};

export const UserRecurringInvestment = () => {
  const {id} = useParams();
  return <RecurringInvestmentPage id={id} />;
};
